/* Adday mobile menu: full-screen native dialog with sliding panels (below 1000px).
   The menu button has aria-expanded. Escape and the close button close it, and
   focus returns to the menu button. An opener can start at a given panel with
   data-adday-mm-start. (Desktop uses the header dropdowns in adday-header.js.)
   Panel triggers have aria-expanded; each panel's Back bar returns to the
   panel above it (data-adday-mm-parent, or the top level).

   The dialog stays rendered while closed (see adday-header.css), so opening
   and closing slide with transform only. The dialog is closed natively only
   after the slide out has finished, so it stays in the top layer throughout. */
(() => {
  const MOBILE_QUERY = window.matchMedia('(max-width: 999px)');
  const LOCK_CLASS = 'adday-mm-lock';
  const VISIBLE_ATTR = 'data-visible';
  const ANIMATING_CLASS = 'is-animating';
  const INSTANT_CLASS = 'adday-mm--instant';
  // Backstop in case transitionend never fires (for example the element is hidden mid-transition).
  const FALLBACK_MS = 500;

  // Runs callback once the element's own transform or opacity transition ends.
  // Returns { cancel } to drop the callback.
  function afterTransition(element, callback) {
    let timer = null;
    const cleanUp = () => {
      element.removeEventListener('transitionend', onEnd);
      window.clearTimeout(timer);
    };
    const onEnd = (event) => {
      if (event.target !== element || event.pseudoElement) return;
      if (event.propertyName !== 'transform' && event.propertyName !== 'opacity') return;
      cleanUp();
      callback();
    };
    element.addEventListener('transitionend', onEnd);
    timer = window.setTimeout(() => {
      cleanUp();
      callback();
    }, FALLBACK_MS);
    return { cancel: cleanUp };
  }

  class AddayMobileMenu {
    constructor(dialog) {
      this.dialog = dialog;
      this.body = dialog.querySelector('.adday-mm__body');
      this.openers = Array.from(document.querySelectorAll(`[data-adday-mm-open][aria-controls="${dialog.id}"]`));
      this.panels = Array.from(dialog.querySelectorAll('[data-adday-mm-panel]'));
      this.topPanel = dialog.querySelector('#AddayMM-top');
      this.currentPanel = this.topPanel;
      this.lastOpener = null;
      this.isOpen = false; // shown, or sliding in
      this.isActive = false; // modal and scroll-locked, until the slide out finishes
      this.openedOnMobile = MOBILE_QUERY.matches;
      this.dialogAnimation = null;
      this.panelAnimation = null;
      this.scrollY = 0;


      this.onBreakpointChange = this.onBreakpointChange.bind(this);
      this.onResize = this.onResize.bind(this);

      this.openers.forEach((opener) => {
        opener.addEventListener('click', (event) => {
          event.preventDefault();
          if (this.isOpen && this.lastOpener === opener) {
            this.close();
          } else {
            this.open(opener);
          }
        });
      });

      dialog.addEventListener('click', (event) => this.onClick(event));
      dialog.addEventListener('cancel', (event) => {
        // Escape: close through our own method so the slide out and clean-up run.
        event.preventDefault();
        this.close();
      });
      // A native close we did not start (for example a second Escape in some browsers).
      dialog.addEventListener('close', () => this.onNativeClose());

      MOBILE_QUERY.addEventListener('change', this.onBreakpointChange);

      this.preloadImages();
    }

    // The panel an opener starts at: its data-adday-mm-start panel, or the top level.
    startPanelFor(opener) {
      const id = opener && opener.dataset.addayMmStart;
      return (id && document.getElementById(id)) || this.topPanel;
    }

    open(opener, { startPanel = null } = {}) {
      if (this.isOpen) return;
      this.isOpen = true;
      if (opener !== undefined) this.lastOpener = opener || null;
      this.openedOnMobile = MOBILE_QUERY.matches;

      if (this.dialogAnimation) {
        // Reopened while sliding out: reverse from where it is.
        this.dialogAnimation.cancel();
        this.dialogAnimation = null;
      }

      this.resetPanels(startPanel || this.startPanelFor(this.lastOpener));

      if (!this.isActive) {
        this.isActive = true;
        this.lockScroll();
        this.dialog.showModal();
      }

      this.startDialogAnimation();
      this.dialog.setAttribute(VISIBLE_ATTR, '');
      this.dialogAnimation = afterTransition(this.dialog, () => {
        this.dialogAnimation = null;
        this.dialog.classList.remove(ANIMATING_CLASS);
      });

      if (this.lastOpener) this.lastOpener.setAttribute('aria-expanded', 'true');
      window.addEventListener('resize', this.onResize);

      const closeButton = this.dialog.querySelector('[data-adday-mm-close]');
      if (closeButton) closeButton.focus({ preventScroll: true });
    }

    close({ instant = false } = {}) {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.dialog.removeAttribute(VISIBLE_ATTR);
      this.openers.forEach((item) => item.setAttribute('aria-expanded', 'false'));
      window.removeEventListener('resize', this.onResize);

      if (this.dialogAnimation) this.dialogAnimation.cancel();
      this.dialogAnimation = null;

      if (instant) {
        this.finishClose();
        return;
      }

      this.startDialogAnimation();
      this.dialogAnimation = afterTransition(this.dialog, () => {
        this.dialogAnimation = null;
        this.finishClose();
      });
    }

    finishClose() {
      if (!this.isActive) return;
      this.isActive = false;
      this.dialog.classList.remove(ANIMATING_CLASS);
      if (this.dialog.open) this.dialog.close();
      this.unlockScroll();
      this.resetPanels(this.topPanel);
      if (this.lastOpener && document.contains(this.lastOpener)) {
        this.lastOpener.focus({ preventScroll: true });
      }
    }

    onNativeClose() {
      if (!this.isActive) return;
      this.isOpen = false;
      this.dialog.removeAttribute(VISIBLE_ATTR);
      this.openers.forEach((item) => item.setAttribute('aria-expanded', 'false'));
      window.removeEventListener('resize', this.onResize);
      if (this.dialogAnimation) this.dialogAnimation.cancel();
      this.dialogAnimation = null;
      this.finishClose();
    }

    // will-change only while the drawer moves.
    startDialogAnimation() {
      this.dialog.classList.add(ANIMATING_CLASS);
    }

    lockScroll() {
      const root = document.documentElement;
      this.scrollY = window.scrollY;
      const scrollbar = window.innerWidth - root.clientWidth;
      if (scrollbar > 0) root.style.setProperty('--adday-mm-scrollbar', `${scrollbar}px`);
      root.classList.add(LOCK_CLASS);
    }

    unlockScroll() {
      const root = document.documentElement;
      root.classList.remove(LOCK_CLASS);
      root.style.removeProperty('--adday-mm-scrollbar');
      if (Math.abs(window.scrollY - this.scrollY) > 1) {
        window.scrollTo({ top: this.scrollY, behavior: 'instant' });
      }
    }

    // Show a panel without animating (used while the drawer is hidden or opening).
    resetPanels(panel) {
      this.dialog.classList.add(INSTANT_CLASS);
      this.showPanel(panel || this.topPanel, { focus: false });
      void this.dialog.offsetWidth; // apply the new positions before transitions return
      this.dialog.classList.remove(INSTANT_CLASS);
      if (this.panelAnimation) this.panelAnimation.cancel();
      this.panelAnimation = null;
      if (this.body) this.body.classList.remove(ANIMATING_CLASS);
    }

    parentOf(panel) {
      if (!panel || panel === this.topPanel) return null;
      const id = panel.dataset.addayMmParent;
      return (id && document.getElementById(id)) || this.topPanel;
    }

    // Panels above the shown one sit to the left, the shown one is current,
    // and every other panel waits to the right. Moving forward slides the new
    // panel in from the right; Back slides it out to the right again.
    showPanel(panel, { focus = true, trigger = null, animate = false } = {}) {
      if (!panel) return;
      const previous = this.currentPanel;

      const chain = [];
      for (let item = panel; item; item = this.parentOf(item)) chain.push(item);

      if (animate && this.body) {
        if (this.panelAnimation) this.panelAnimation.cancel();
        this.body.classList.add(ANIMATING_CLASS);
        this.panelAnimation = afterTransition(panel, () => {
          this.panelAnimation = null;
          this.body.classList.remove(ANIMATING_CLASS);
        });
      }

      this.panels.forEach((item) => {
        let state = 'right';
        if (item === panel) state = 'current';
        else if (chain.includes(item)) state = 'left';
        item.dataset.state = state;
        item.inert = item !== panel;
      });

      // A trigger is expanded while its panel is shown or is above the shown one.
      this.dialog.querySelectorAll('[data-adday-mm-open-panel]').forEach((item) => {
        const target = document.getElementById(item.dataset.addayMmOpenPanel);
        item.setAttribute('aria-expanded', chain.includes(target) ? 'true' : 'false');
      });

      if (trigger) panel.addayTrigger = trigger;
      this.currentPanel = panel;
      if (panel !== previous) panel.scrollTop = 0;

      if (!focus) return;
      if (chain.includes(previous) || !previous) {
        // Moved forward: focus the new panel's Back bar, or its first row if Back is hidden.
        const back = panel.querySelector('[data-adday-mm-back]');
        const target = back && back.offsetParent !== null ? back : panel.querySelector('a, button');
        if (target) target.focus({ preventScroll: true });
      } else if (previous && previous.addayTrigger) {
        // Went back: return focus to the row that opened the panel we left.
        previous.addayTrigger.focus({ preventScroll: true });
      }
    }

    onClick(event) {
      // A click on the overlay (the ::backdrop), if any shows, reaches the dialog itself.
      if (event.target === this.dialog) {
        this.close();
        return;
      }

      const panelTrigger = event.target.closest('[data-adday-mm-open-panel]');
      if (panelTrigger) {
        const panel = document.getElementById(panelTrigger.dataset.addayMmOpenPanel);
        this.showPanel(panel, { trigger: panelTrigger, animate: true });
        return;
      }

      if (event.target.closest('[data-adday-mm-back]')) {
        this.showPanel(this.parentOf(this.currentPanel) || this.topPanel, { animate: true });
        return;
      }

      if (event.target.closest('[data-adday-mm-close]')) {
        this.close();
        return;
      }

      // Choosing any link (including the basket and the Taster Pack card) closes the menu.
      if (event.target.closest('a')) {
        this.lastOpener = null;
        this.close();
      }
    }

    // The menu is hidden from 1000px, so close at once when the window crosses it.
    onBreakpointChange() {
      this.close({ instant: true });
    }

    // Backstop for browsers that do not fire the media query change event.
    onResize() {
      if (MOBILE_QUERY.matches !== this.openedOnMobile) this.close({ instant: true });
    }

    // Load the menu's images while the page is idle, so they are ready
    // before the menu opens instead of popping in as it slides.
    preloadImages() {
      const run = () => {
        this.dialog.querySelectorAll('img[loading="lazy"]').forEach((img) => {
          img.loading = 'eager';
        });
      };
      const schedule = () => {
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(run, { timeout: 3000 });
        } else {
          window.setTimeout(run, 1500);
        }
      };
      if (document.readyState === 'complete') {
        schedule();
      } else {
        window.addEventListener('load', schedule, { once: true });
      }
    }

    // Theme editor: show a block's panel while it is selected (mobile only).
    openBlock(blockId) {
      if (!MOBILE_QUERY.matches) return;
      const panel = this.dialog.querySelector(`[data-adday-mm-panel][data-block-id="${blockId}"]`);
      if (!panel) return;
      const opener = this.openers.find((item) => !item.dataset.addayMmStart);
      if (this.isOpen) {
        this.showPanel(panel, { focus: false });
      } else {
        this.open(opener || null, { startPanel: panel });
      }
    }

    destroy() {
      this.close({ instant: true });
      MOBILE_QUERY.removeEventListener('change', this.onBreakpointChange);
    }
  }

  let instance = null;

  function init() {
    if (instance) instance.destroy();
    const dialog = document.querySelector('[data-adday-mm]');
    instance = dialog && typeof dialog.showModal === 'function' ? new AddayMobileMenu(dialog) : null;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', init);
    document.addEventListener('shopify:block:select', (event) => {
      if (instance) instance.openBlock(event.detail.blockId);
    });
    document.addEventListener('shopify:block:deselect', () => {
      if (instance) instance.close({ instant: true });
    });
  }
})();
